import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTheme } from '../UI/Theme/ThemeContext';

// Timeline: desktop horizontal (top of cards) + mobile vertical (left of stacked cards)
// Props: phases = [{ phaseName, progress?, completed?, durationWeeks? }, ...]

const Wrap = styled.div`
  width: 100%;
  margin-bottom: 8px;
  position: relative;

  .desktop-only { display: block; }
  .mobile-only { display: none; }

  @media (max-width:900px) {
    .desktop-only { display: none; }
    .mobile-only { display: block; }
  }
`;

const DesktopSVGWrap = styled.div`
  width: 100%;
  height: 86px;
  display:flex;
  align-items:center;
  justify-content:center;
  position: relative;
`;

const MobileSVGWrap = styled.div`
  width: 100%;
  display:flex;
  align-items:flex-start;
  justify-content:center;
`;

const Timeline = ({ phases = [], mobileHeight }) => {
  const desktopPathRef = useRef(null);
  const mobilePathRef = useRef(null);
  const sparkRef = useRef(null);

  const { themeName } = useTheme();
  const isDarkMode = themeName === 'dark';

  // compute an overall percent based on average phase progress
  const avgPercent = (() => {
    if (!phases || phases.length === 0) return 0;
    const nums = phases.map(p => {
      if (typeof p.progress === 'number') return Math.max(0, Math.min(100, Number(p.progress)));
      if (p.completed) return 100;
      return 0;
    });
    const sum = nums.reduce((a,b)=>a+b,0);
    return Math.round(sum / nums.length);
  })();

  useEffect(() => {
    // Desktop path fill animation
    const path = desktopPathRef.current;
    if (path) {
      const total = path.getTotalLength();
      // ensure no jump on first render
      path.style.transition = 'stroke-dashoffset 900ms ease-in-out, stroke 400ms linear';
      path.style.strokeDasharray = total;
      const offset = Math.max(0, total * (1 - avgPercent/100));
      // trigger in next frame to allow transition
      requestAnimationFrame(() => { path.style.strokeDashoffset = offset; });

      // position a small spark dot along the path (if exists)
      const spark = sparkRef.current;
      if (spark) {
        try {
          const pt = path.getPointAtLength(total * (avgPercent/100));
          spark.setAttribute('cx', pt.x);
          spark.setAttribute('cy', pt.y);
          spark.style.opacity = avgPercent > 0 ? '1' : '0';
        } catch (e) { /* no-op on errors */ }
      }
    }

    // Mobile path animation (vertical)
    const mpath = mobilePathRef.current;
    if (mpath) {
      const total = mpath.getTotalLength();
      mpath.style.transition = 'stroke-dashoffset 900ms ease-in-out, stroke 400ms linear';
      mpath.style.strokeDasharray = total;
      const offset = Math.max(0, total * (1 - avgPercent/100));
      requestAnimationFrame(() => { mpath.style.strokeDashoffset = offset; });
    }
  }, [avgPercent, phases]);

  // layout math - nodes include a Start node before phases
  const nodesCount = Math.max(1, phases.length + 1); // start + phases
  const count = nodesCount;
  // compute mobile svg height (H) and spacing before render so svg gets correct height
  const defaultMobileH = Math.max(120, 80 + (phases.length - 1) * 90);
  const H = Number(mobileHeight) > 80 ? Number(mobileHeight) : defaultMobileH;
  const spacingForMobile = (H - 40) / Math.max(1, nodesCount - 1);
  

  return (
    <Wrap aria-hidden>
      {/* Desktop horizontal timeline */}
      <DesktopSVGWrap className="desktop-only">
        <svg width="100%" height="86" viewBox="0 0 1200 86" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="tlGrad" x1="0" x2="1">
              <stop offset="0%" stopColor="#3E8F93" />
              <stop offset="100%" stopColor="#2a6f6d" />
            </linearGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* background faint track */}
          <path d="M40 60 L1160 60" stroke="#eee" strokeWidth="6" strokeLinecap="round" />

          {/* foreground animated track */}
          <path ref={desktopPathRef} d="M40 60 L1160 60" stroke="url(#tlGrad)" strokeWidth="8" strokeLinecap="round" strokeDasharray="0" strokeDashoffset="0" />

          {/* spark (moving indicator) */}
          <circle ref={sparkRef} r="6" fill="#fff" stroke="#3E8F93" strokeWidth="2" style={{ filter: 'url(#glow)', transition: 'cx 900ms linear, cy 900ms linear, opacity 300ms linear' }} />

          {/* nodes including Start */}
          {[...Array(count)].map((_, i) => {
            // map node index to phase index: node 0 = Start, node 1 = phase 0, ...
            const x = 40 + (1120) * (i / Math.max(1, count - 1));
            const isStart = i === 0;
            const isLastNode = i === (count - 1);
            const phaseIndex = i - 1;
            const p = phases[phaseIndex] || {};
            const completed = isStart ? true : (p.completed || (typeof p.progress === 'number' && p.progress >= 100));
            const percent = isStart ? 100 : (typeof p.progress === 'number' ? p.progress : (completed ? 100 : 0));
            const isActive = !isStart && percent > 0 && percent < 100;
            const label = isStart ? 'Start' : (isLastNode ? '' : String(i));
            return (
              <g key={i} transform={`translate(${x},60)`}> 
                {isLastNode ? (
                  // black-and-white flag marker for desktop final node
                  <g aria-hidden transform="translate(0,-6)">
                    <rect x="-1" y="-18" width="2" height="36" fill="#000" />
                    <path d="M2 -16 L24 -10 L2 -4 Z" fill="crimson" stroke="#fff" strokeWidth="1" />
                  </g>
                ) : (
                  <circle r="12" fill={completed ? '#41a956' : (isActive ? '#f6b32d' : '#ffffff')} stroke={completed ? '#2f8b4a' : '#dcdcdc'} strokeWidth="2" />
                )}
                <text x="0" y="-20" fontSize="11" fontWeight="700" textAnchor="middle" fill={isLastNode ? '#e53935' : (isDarkMode ? '#ffffff' : '#222')}>{label}</text>
              </g>
            )
          })}

          {/* durations between nodes */}
          {[...Array(Math.max(0, count-1))].map((_, i) => {
            // duration for segment i -> i+1 corresponds to phases[i] (phase durations)
            const p = phases[i] || {};
            const dur = p.durationWeeks || null;
            if (!dur) return null;
            const x1 = 40 + (1120) * (i / Math.max(1, count - 1));
            const x2 = 40 + (1120) * ((i+1) / Math.max(1, count - 1));
            const cx = (x1 + x2) / 2;
            return (
              <text key={'d'+i} x={cx} y={42} fontSize="11" textAnchor="middle" fill={isDarkMode ? '#cfcfcf' : '#6b6b6b'}>{dur} wk{dur>1?'s':''}</text>
            )
          })}
        </svg>
      </DesktopSVGWrap>

      {/* Mobile vertical timeline */}
      <MobileSVGWrap className="mobile-only">
          <svg width="64" height={H} viewBox={`0 0 64 ${H}`} xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="tlGradV" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#3E8F93" />
              <stop offset="100%" stopColor="#2a6f6d" />
            </linearGradient>
            <filter id="glowV" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* vertical background track */}
            {(() => {
              const spacing = spacingForMobile;
              const endY = 20 + (nodesCount - 1) * spacing;
              return (
                <>
                  <path d={`M32 20 L32 ${endY}`} stroke="#eee" strokeWidth="4" strokeLinecap="round" />

                  {/* animated foreground track */}
                  <path ref={mobilePathRef} d={`M32 20 L32 ${endY}`} stroke="url(#tlGradV)" strokeWidth="6" strokeLinecap="round" strokeDasharray="0" strokeDashoffset="0" />

                  {/* nodes (Start + phases) */}
                  {Array.from({ length: nodesCount }).map((_, i) => {
                    const y = 20 + i * spacing;
                    const isStart = i === 0;
                    const isLastNode = i === (nodesCount - 1);
                    const phaseIndex = i - 1;
                    const p = phases[phaseIndex] || {};
                    const completed = isStart ? true : (p.completed || (typeof p.progress === 'number' && p.progress >= 100));
                    const percent = isStart ? 100 : (typeof p.progress === 'number' ? p.progress : (completed ? 100 : 0));
                    const isActive = !isStart && percent > 0 && percent < 100;
                    // Mobile labels: use numbers for phase nodes (1..N-1); keep Start and Goal for first/last
                    const label = isStart ? 'Start' : (isLastNode ? 'Goal' : String(i));
                    return (
                      <g key={'m'+i} transform={`translate(32, ${y})`}>
                        {isLastNode ? (
                          // black-and-white flag marker for goal node
                          <g aria-hidden transform="translate(0,-6)">
                            <rect x="-1" y="-14" width="2" height="28" fill="#000" />
                            <path d="M2 -12 L14 -8 L2 -4 Z" fill="#000" stroke="#fff" strokeWidth="0.8" />
                          </g>
                        ) : (
                          <circle r="6" fill={completed ? '#41a956' : (isActive ? '#f6b32d' : '#ffffff')} stroke={completed ? '#2f8b4a' : '#dcdcdc'} strokeWidth="2" />
                        )}
                        <text x="16" y="5" fontSize="12" textAnchor="start" fill={isLastNode ? '#e53935' : (isDarkMode ? '#ffffff' : '#222')}>{label}</text>
                      </g>
                    )
                  })}

                  {/* durations text between nodes (to the right) */}
                  {Array.from({ length: Math.max(0, nodesCount-1) }).map((_, i) => {
                    const p = phases[i] || {};
                    const dur = p.durationWeeks || null;
                    if (!dur) return null;
                    const y1 = 20 + i * spacing;
                    const y2 = 20 + (i+1) * spacing;
                    const cy = (y1 + y2) / 2;
                    return (
                      <text key={'md'+i} x={16} y={cy+4} fontSize="11" textAnchor="start" fill={isDarkMode ? '#cfcfcf' : '#6b6b6b'}>{dur} wk{dur>1?'s':''}</text>
                    )
                  })}

                  {/* little goal icon at bottom */}
                  <g transform={`translate(8, ${endY + 18})`}>
                    <rect x="0" y="0" width="48" height="22" rx="6" fill="#3E8F93" opacity="0.08" />
                    <text x="24" y="14" fontSize="11" textAnchor="middle" fill="#2a6f6d">Goal</text>
                  </g>
                </>
              )
            })()}
        </svg>
      </MobileSVGWrap>
    </Wrap>
  );
};

export default Timeline;
